import React, { useState } from 'react'
import { HsThreshold } from './'
import { ambient } from '@aw_products/ambient-weather-common'
import { path } from 'ramda'
import { Loader } from '../../components'
// import PropTypes from 'prop-types';

const guidelines = ambient.HST.guidelines

const createThreshold = (zoneIndex = 0) => {
  const standardColors = ['#40ae49', '#fff101', '#f68e1e', '#d1232a', '#231f20']
  return {
    _id: Math.random(),
    color: standardColors[zoneIndex % standardColors.length] || '#808080'
  }
}
const defaultHst = () => ({
  guideline: '',
  enabledThresholds: [],
  customGuideline: {
    zones: [createThreshold()]
  }
})

export default function HeatStressEdit ({ currentDevice, deviceActions, alertActions }) {
  const [hst, _setHst] = useState(path(['settings', 'hst'], currentDevice) || defaultHst())
  const [loading, setLoading] = useState(false)
  const [hasChanged, setHasChanged] = useState(false)
  const setHst = hst => {
    let guideline
    if (hst.guideline === '') {
      hst.enabledThresholds = []
    } else if (hst.guideline === 'custom') {
      guideline = hst.customGuideline
      // make sue enabled thresholds actually exist in thresholds
      hst.enabledThresholds = hst.enabledThresholds.filter(et => guideline.zones.map(t => t.above).includes(et)).sort((a, b) => a - b)
    } else {
      guideline = ambient.HST.guidelines[hst.guideline]
      // set default level and modifier
      if (guideline.levelLabel) {
        hst.level = hst.level || guideline.guidelines[0].label
        guideline = guideline.guidelines.find(g => g.label === hst.level)
      }
      if (guideline.modifier) {
        hst.modifier = hst.modifier || guideline.modifier.choices[0].label
      }
    }
    _setHst(hst)
    setHasChanged(true)
  }
  const isCustom = hst.guideline === 'custom'
  const guideline = isCustom ? hst.customGuideline : guidelines[hst.guideline]
  let finalGuideline = guideline
  // const hasChanged = path(['settings', 'hst', 'guideline'], currentDevice) !== hst.guideline ||
  //   symmetricDifference(path(['settings', 'hst', 'enabledThresholds'], currentDevice) || [], hst.enabledThresholds).length > 0 || hst.customGuideline !== path(['settings', 'hst', 'customGuideline'], currentDevice)

  const save = async () => {
    const settings = { ...currentDevice.settings }
    settings.hst = hst
    settings.hst._notifyRw = true // notify rw to update the device
    settings.hst.enabledThresholds = hst.enabledThresholds.map(parseFloat)
    if (isCustom) {
      settings.hst.customGuideline.zones = hst.customGuideline.zones.map(t => {
        t.above = Math.round(parseFloat(t.above) * 10) / 10
        return t
      })
      const isBadThreshold = threshold => {
        if (isNaN(threshold.above) || threshold.above === '' || threshold.above === null) {
          return true
        }
        if (threshold.above < -50 || threshold.above > 150) {
          return true
        }
        return false
      }
      if (hst.customGuideline.zones.some(isBadThreshold)) {
        return window.alert('Please enter a valid temperature for each threshold')
      }
      // Validate color fields
      const hasInvalidColor = hst.customGuideline.zones.some(zone => {
        if (zone.color && !/^#[0-9A-Fa-f]{6}$/.test(zone.color)) {
          return true
        }
        return false
      })
      if (hasInvalidColor) {
        return window.alert('Please ensure all zone colors are valid hex colors')
      }
    }
    // are there subscribers?
    const currentGuideline = path(['settings', 'hst', 'guideline'], currentDevice)
    if (currentGuideline && currentDevice.public) {
      setLoading(true)
      if (window.confirm('Note, your station is configured as public on AWN.  Other AWN members may have set up their own zone alerts on your station based on your current guideline selections.  Any changes to your guidelines will change the zone thresholds, so any such existing alerts would be deleted and the owner alerted of that. Continue?')) {
        settings.hst._deleteAlerts = true
      } else {
        setLoading(false)
        return
      }
      setLoading(false)
    }
    setHasChanged(false)
    deviceActions.patch(currentDevice._id, { settings })
  }
  let levelChooser, modifierChooser
  if (guideline && guideline.guidelines) {
    if (guideline.guidelines.length > 1) {
      finalGuideline = guideline.guidelines.find(g => g.label === hst.level)
      levelChooser = (
        <div className='level'>
          <label>{guideline.levelLabel}</label>
          <select
            value={hst.level}
            className='form-control'
            onChange={evt => {
              setHst({ ...hst, level: evt.target.value, modifier: null, enabledThresholds: [] })
            }}
          >
            {guideline.guidelines.map(g => <option key={g.label} value={g.label}>{g.label}</option>)}
          </select>
        </div>
      )
    } else {
      finalGuideline = guideline.guidelines[0]
    }
    if (finalGuideline.modifier) {
      modifierChooser = (
        <div className='modifier'>
          <label>{finalGuideline.modifier.label}</label>
          <select
            value={hst.modifier}
            className='form-control'
            onChange={evt => {
              setHst({ ...hst, modifier: evt.target.value, enabledThresholds: [] })
            }}
          >
            {finalGuideline.modifier.choices.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
        </div>
      )
    }
  }

  return (
    <div className='device-heat-stress-edit'>
      <label>Guideline</label>
      <select
        value={hst.guideline}
        className='form-control'
        onChange={evt => {
          setHst({
            ...hst,
            guideline: evt.target.value,
            level: null,
            enabledThresholds: [],
            modifier: null
          })
        }}
      >
        <option value=''>None</option>
        <option value='custom'>Custom</option>
        {Object.keys(guidelines).map(g => <option key={g} value={g}>{guidelines[g].label}</option>)}
      </select>
      <div className='sub-levels'>
        {levelChooser}
        {modifierChooser}
      </div>
      <div className='guideline'>
        {finalGuideline && (
          finalGuideline.zones.map((zone, i) => <HsThreshold key={isCustom ? zone._id : zone.label} zone={zone} setHst={setHst} hst={hst} editable={isCustom} guideline={finalGuideline} />)
        )}
        {isCustom && hst.customGuideline.zones.length < 10 && (
          <a className='add-threshold hs-threshold threshold' onClick={() => setHst({ ...hst, customGuideline: { ...hst.customGuideline, zones: [...hst.customGuideline.zones, createThreshold(hst.customGuideline.zones.length)] } })} />
        )}
      </div>
      {hasChanged && (
        loading ? <Loader /> : <button className='btn btn-primary' onClick={save}>Save</button>
      )}

    </div>
  )
};

HeatStressEdit.propTypes = {}
HeatStressEdit.defaultProps = {}

HeatStressEdit.displayName = 'HeatStressEdit'
